import React, { useState, useEffect } from "react";
import axios from "axios";
import './Users.css';
import config from "../config";

import i18n from "../i18n";
const tr = i18n.t.bind(i18n);

// Μεταδεδομένα δικαιωμάτων: [πεδίο, κλειδί μετάφρασης] — η σειρά καθορίζει την εμφάνιση
const PERM_META = [
  ["view_admin", "Administrator"],
  ["view_reservations_list", "Reservations List"],
  ["view_reservation_form", "Reservation Form"],
  ["view_bbq", "BBQ"],
  ["view_kitchen", "Kitchen"],
  ["view_service", "Service"],
  ["view_schedule", "Schedule"],
  ["view_scheduler", "Scheduler"],
  ["view_reports", "Reports"],
  ["view_clients", "Clients"],
  ["view_settings", "Settings"],
  ["view_tables", "Tables"],
  ["view_users", "Users"],
  ["view_billing", "Billing"],
  ["view_orders", "Orders"],
  ["view_bar", "Bar"],
  ["view_salads", "Salads"],
  ["view_desserts", "Desserts"],
];
const ROLE_META = [
  ["at_management", "@ Management"],
  ["at_bbq", "@ BBQ"],
  ["at_kitchen", "@ Kitchen"],
  ["at_cleaning", "@ Cleaning"],
  ["at_service", "@ Service"],
  ["push_notifications", "Service Notifications"],
];
const ALL_FLAGS = [...PERM_META.map(([p]) => p), ...ROLE_META.map(([p]) => p)];

const EMPTY_USER = ALL_FLAGS.reduce(
  (o, p) => ({ ...o, [p]: false }),
  { id: null, invite: false, username: "", password: "", name: "", phone: "", email: "", comment: "" }
);

// Κανόνες εξάρτησης δικαιωμάτων (ίδιοι με πριν — μία υλοποίηση για όλα τα views)
function applyDeps(u, perm) {
  const x = { ...u };
  if (perm === "view_reservation_form" && x.view_reservation_form) x.view_reservations_list = true;
  else if (perm === "view_reservations_list" && !x.view_reservations_list) x.view_reservation_form = false;
  else if (perm === "push_notifications" && x.push_notifications) x.at_service = true;
  else if (perm === "at_service" && !x.at_service) x.push_notifications = false;
  return x;
}

const bit = (v) => !!v?.data?.[0];

function Users() {
  const [users, setUsers] = useState([]);
  const [planPerms, setPlanPerms] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendInfo, setSendInfo] = useState(null);
  // Δύο προβολές: κάρτες (ευανάγνωστη) + πίνακας (μακροσκοπική ματιά) — θυμάται την επιλογή
  const [view, setView] = useState(() => localStorage.getItem("estiasync_users_view") || "cards");
  const [modal, setModal] = useState(null); // { mode: 'create'|'edit', draft }
  const [busy, setBusy] = useState(false);

  const switchView = (v) => {
    setView(v);
    try { localStorage.setItem("estiasync_users_view", v); } catch {}
  };

  useEffect(() => { fetchUsers(); }, []);

  useEffect(() => {
    axios.get(`${config.API_BASE_URL}/users/plan-permissions`)
      .then((res) => {
        const { active, permissions } = res.data || {};
        if (!active || !permissions) { setPlanPerms({}); return; }
        const normalize = (v) => {
          if (v === null || v === undefined) return false;
          if (v && v.data) return !!v.data[0];
          return !!v;
        };
        const out = {};
        PERM_META.forEach(([p]) => { if (p !== "view_admin") out[p] = normalize(permissions[p]); });
        setPlanPerms(out);
      })
      .catch((err) => {
        console.error("Error fetching plan-permissions:", err);
        setPlanPerms({});
      });
  }, []);

  const fetchUsers = () => {
    axios.get(`${config.API_BASE_URL}/users`)
      .then((response) => {
        setUsers(response.data.map((user) => ({
          id: user.id,
          username: user.username || "",
          password: "",
          name: user.name || "",
          phone: user.phone || "",
          email: user.email || "",
          comment: user.comment || "",
          ...ALL_FLAGS.reduce((o, p) => ({ ...o, [p]: bit(user[p]) }), {}),
        })));
      })
      .catch((error) => console.error("Error fetching users:", error));
  };

  const isViewPermVisible = (perm) => {
    if (perm === "view_admin") return true;
    if (!planPerms) return true;
    return !!planPerms[perm];
  };
  const visiblePermMeta = PERM_META.filter(([p]) => isViewPermVisible(p));
  const showSchedulerFeatures = !planPerms || !!planPerms.view_scheduler;
  const visibleRoleMeta = showSchedulerFeatures ? ROLE_META : [];

  // ---------- Ενέργειες ----------

  const saveModal = async () => {
    if (!modal) return;
    setBusy(true);
    try {
      if (modal.mode === "create") {
        const response = await axios.post(`${config.API_BASE_URL}/users`, modal.draft);
        if (response.data.error) { alert(response.data.error); return; }
        if (response.data.invited) alert(tr('Invitation email sent.'));
        else if (response.data.inviteError) alert(tr('The user was created but the invitation email failed — use ✉️ to resend it.'));
      } else {
        const response = await axios.put(`${config.API_BASE_URL}/users/${modal.draft.id}`, modal.draft);
        if (response.data.error) { alert(response.data.error); return; }
      }
      setModal(null);
      fetchUsers();
    } catch (error) {
      alert(error?.response?.data?.error || tr('Something went wrong — please try again.'));
    } finally { setBusy(false); }
  };

  // Άμεσο toggle από τον πίνακα (auto-save) — ίδιοι κανόνες, χωρίς modal
  const quickToggle = async (user, perm) => {
    const updated = applyDeps({ ...user, [perm]: !user[perm] }, perm);
    setUsers((us) => us.map((u) => (u.id === user.id ? updated : u))); // optimistic
    try {
      const response = await axios.put(`${config.API_BASE_URL}/users/${user.id}`, updated);
      if (response.data.error) alert(response.data.error);
    } catch (error) {
      alert(error?.response?.data?.error || tr('Something went wrong — please try again.'));
    } finally { fetchUsers(); }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm(tr('Are you sure you want to delete this user?'))) return;
    try {
      const response = await axios.delete(`${config.API_BASE_URL}/users/${id}`);
      if (response.data.error) alert(response.data.error);
      fetchUsers();
    } catch (error) {
      alert(error?.response?.data?.error || tr('Something went wrong — please try again.'));
    }
  };

  const handleSendInvite = async (userId) => {
    try {
      await axios.post(`${config.API_BASE_URL}/auth/invite`, { userId });
      alert(tr('Invitation email sent.'));
    } catch (error) {
      alert(error?.response?.data?.error === 'no-email'
        ? tr('This user has no email address.')
        : tr('Something went wrong — please try again.'));
    }
  };

  async function handleNotifyAll() {
    if (!window.confirm("Send a test notification to all subscribers in this company?")) return;
    try {
      setSending(true);
      setSendInfo(null);
      const resp = await axios.post(`${config.API_BASE_URL}/notifications/test-all`, {}, { withCredentials: true });
      setSendInfo(resp.data);
    } catch (e) {
      console.error("Notify error:", e);
      setSendInfo({ error: e.response?.data?.error || "Failed to send" });
    } finally { setSending(false); }
  }

  // ---------- Βοηθητικά εμφάνισης ----------

  const initialOf = (u) => (u.name || u.username || "?").trim().charAt(0).toUpperCase();
  const grantedChips = (u) => {
    if (u.view_admin) return [tr('Full access')];
    return visiblePermMeta.filter(([p]) => p !== "view_admin" && u[p]).map(([, k]) => tr(k));
  };
  const rolesOf = (u) => visibleRoleMeta.filter(([p]) => u[p]).map(([, k]) => tr(k));

  const openCreate = () => setModal({ mode: "create", draft: { ...EMPTY_USER, invite: true } });
  const openEdit = (u) => setModal({ mode: "edit", draft: { ...u, password: "" } });

  // Πρόσκληση: αρκεί το email (username/κωδικό τα ορίζει ο χρήστης στην ενεργοποίηση)
  const canSave = modal && (
    modal.mode === "create" && modal.draft.invite
      ? !!modal.draft.email
      : modal.draft.username && (modal.mode === "edit" || modal.draft.password)
  );

  // ---------- Render ----------

  return (
    <div className="usrPage">
      <div className="usrHeader">
        <h2>{tr('Users Management')}</h2>
        <div className="usrHeaderActions">
          <div className="usrViewToggle" role="tablist">
            <button className={view === "cards" ? "on" : ""} onClick={() => switchView("cards")}>👥 {tr('Cards')}</button>
            <button className={view === "matrix" ? "on" : ""} onClick={() => switchView("matrix")}>📊 {tr('Matrix')}</button>
          </div>
          {showSchedulerFeatures && (
            <button className="notify-btn" onClick={handleNotifyAll} disabled={sending}>
              {sending ? "Notifying..." : "Send Test Notifications"}
            </button>
          )}
          <button className="usrPrimaryBtn" onClick={openCreate}>➕ {tr('New user')}</button>
        </div>
      </div>

      {sendInfo && (
        <div className="notify-result">
          {sendInfo.ok
            ? <>{tr('Sent:')}<b>{sendInfo.success}</b> {tr('• Failed:')}<b>{sendInfo.failure}</b> {tr('• Total:')}<b>{sendInfo.total}</b></>
            : <>{tr('Error:')}{sendInfo.error}</>}
        </div>
      )}

      {users.length === 0 && <p className="usrHint">{tr('No users yet.')}</p>}

      {view === "cards" && (
        <div className="usrGrid">
          {users.map((u) => {
            const chips = grantedChips(u);
            const roles = rolesOf(u);
            return (
              <div key={u.id} className={`usrCard${u.view_admin ? " admin" : ""}`}>
                <div className="usrCardTop">
                  <div className={`usrAvatar${u.view_admin ? " admin" : ""}`}>{initialOf(u)}</div>
                  <div className="usrIdentity">
                    <div className="usrName">{u.name || u.username}</div>
                    <div className="usrUsername">@{u.username}</div>
                  </div>
                  <div className="usrCardActions">
                    <button title={tr('Edit user')} onClick={() => openEdit(u)}>✏️</button>
                    {u.email && <button title={tr('Send invitation email')} onClick={() => handleSendInvite(u.id)}>✉️</button>}
                    <button title={tr('Delete')} onClick={() => handleDeleteUser(u.id)}>🗑️</button>
                  </div>
                </div>
                {(u.email || u.phone) && (
                  <div className="usrContact">
                    {u.email && <span>📧 {u.email}</span>}
                    {u.phone && <span>📞 {u.phone}</span>}
                  </div>
                )}
                <div className="usrChips">
                  {chips.length === 0 && <span className="usrChip none">—</span>}
                  {chips.slice(0, 6).map((c) => (
                    <span key={c} className={`usrChip${u.view_admin ? " gold" : ""}`}>{c}</span>
                  ))}
                  {chips.length > 6 && <span className="usrChip more">+{chips.length - 6}</span>}
                </div>
                {roles.length > 0 && <div className="usrRoles">🧑‍🍳 {roles.join(" · ")}</div>}
                {u.comment && <div className="usrComment">💬 {u.comment}</div>}
              </div>
            );
          })}
        </div>
      )}

      {view === "matrix" && (
        <div className="usrMatrixWrap">
          <table className="usrMatrix">
            <thead>
              <tr>
                <th className="usrMatrixUser">{tr('Users')}</th>
                {visiblePermMeta.map(([p, k]) => (
                  <th key={p} className={`usrMatrixCol${p === "view_admin" ? " admin" : ""}`}>
                    <span>{tr(k)}</span>
                  </th>
                ))}
                {visibleRoleMeta.map(([p, k]) => (
                  <th key={p} className="usrMatrixCol role"><span>{tr(k)}</span></th>
                ))}
                <th className="usrMatrixActions" />
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className={u.view_admin ? "admin" : ""}>
                  <td className="usrMatrixUser">
                    <div className="usrName">{u.name || u.username}</div>
                    <div className="usrUsername">@{u.username}</div>
                  </td>
                  {[...visiblePermMeta, ...visibleRoleMeta].map(([p, k], i) => (
                    <td key={p}>
                      <input
                        type="checkbox"
                        className="usrMatrixCheck"
                        checked={!!u[p]}
                        title={`${u.name || u.username} — ${tr(k)}`}
                        disabled={i > 0 && u.view_admin}
                        onChange={() => quickToggle(u, p)}
                      />
                    </td>
                  ))}
                  <td className="usrMatrixActions">
                    <button title={tr('Edit user')} onClick={() => openEdit(u)}>✏️</button>
                    {u.email && <button title={tr('Send invitation email')} onClick={() => handleSendInvite(u.id)}>✉️</button>}
                    <button title={tr('Delete')} onClick={() => handleDeleteUser(u.id)}>🗑️</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="usrHint">
            {tr('* At least one \'Administartor\' needed! Administrators have full access.')}{' '}
            {tr('* Access to \'Reservation Form\' requires access to \'Reservations List\' too.')}
            {showSchedulerFeatures && <> {tr('* Push notifications requires a role @ Service too.')}</>}
          </p>
        </div>
      )}

      {modal && (
        <div className="usrModalOverlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setModal(null); }}>
          <div className="usrModal">
            <div className="usrModalHead">
              <h3>{modal.mode === "create" ? `➕ ${tr('New user')}` : `✏️ ${tr('Edit user')}`}</h3>
              <button className="usrModalClose" onClick={() => setModal(null)}>✕</button>
            </div>

            <div className="usrModalSection">
              <h4>{tr('User Details')}</h4>
              {modal.mode === "create" && (
                <label className="usrInviteRow top"
                  title={tr('An email invitation will be sent — the user sets their own password or connects Google. Requires the Email field.')}>
                  <input type="checkbox" checked={!!modal.draft.invite}
                    onChange={(e) => setModal({
                      ...modal,
                      draft: { ...modal.draft, invite: e.target.checked, ...(e.target.checked ? { username: "", password: "" } : {}) },
                    })} />
                  📧 {tr('Invite by email')}
                  <span className="usrHint"> — {tr('The user sets their own credentials via the email invitation.')}</span>
                </label>
              )}
              <div className="usrFormGrid">
                {/* Με πρόσκληση, username & κωδικό τα διαλέγει ο ίδιος ο χρήστης στην ενεργοποίηση */}
                {!(modal.mode === "create" && modal.draft.invite) && (
                  <>
                    <label>{tr('Username')} *
                      <input type="text" autoCapitalize="none" autoCorrect="off" value={modal.draft.username}
                        onChange={(e) => setModal({ ...modal, draft: { ...modal.draft, username: e.target.value } })} />
                    </label>
                    <label>{tr('Password')}{modal.mode === "create" ? " *" : ""}
                      <input type="password" autoComplete="new-password"
                        placeholder={modal.mode === "edit" ? tr('Set new password') : tr('Password')}
                        value={modal.draft.password}
                        onChange={(e) => setModal({ ...modal, draft: { ...modal.draft, password: e.target.value } })} />
                    </label>
                  </>
                )}
                <label>{tr('Name')}
                  <input type="text" value={modal.draft.name}
                    onChange={(e) => setModal({ ...modal, draft: { ...modal.draft, name: e.target.value } })} />
                </label>
                <label>{tr('Phone')}
                  <input type="text" value={modal.draft.phone}
                    onChange={(e) => setModal({ ...modal, draft: { ...modal.draft, phone: e.target.value } })} />
                </label>
                <label>{tr('Email')}{modal.mode === "create" && modal.draft.invite ? " *" : ""}
                  <input type="text" value={modal.draft.email}
                    onChange={(e) => setModal({ ...modal, draft: { ...modal.draft, email: e.target.value } })} />
                </label>
                <label>{tr('Comment')}
                  <input type="text" value={modal.draft.comment}
                    onChange={(e) => setModal({ ...modal, draft: { ...modal.draft, comment: e.target.value } })} />
                </label>
              </div>
              {modal.mode === "edit" && (
                <p className="usrHint">{tr('Optional — leave the password empty to keep the current one.')}</p>
              )}
            </div>

            <div className="usrModalSection">
              <h4>{tr('View Permissions')}</h4>
              <label className="usrAdminToggle">
                <input type="checkbox" checked={!!modal.draft.view_admin}
                  onChange={(e) => setModal({ ...modal, draft: { ...modal.draft, view_admin: e.target.checked } })} />
                👑 {tr('Administrator')} <span className="usrHint">— {tr('Full access')}</span>
              </label>
              <div className={`usrPermGrid${modal.draft.view_admin ? " off" : ""}`}>
                {visiblePermMeta.filter(([p]) => p !== "view_admin").map(([p, k]) => (
                  <label key={p} className="usrPerm">
                    <input type="checkbox" checked={!!modal.draft[p]} disabled={!!modal.draft.view_admin}
                      onChange={() => setModal({ ...modal, draft: applyDeps({ ...modal.draft, [p]: !modal.draft[p] }, p) })} />
                    {tr(k)}
                  </label>
                ))}
              </div>
            </div>

            {showSchedulerFeatures && (
              <div className="usrModalSection">
                <h4>{tr('Employee Roles')}</h4>
                <div className={`usrPermGrid${modal.draft.view_admin ? " off" : ""}`}>
                  {ROLE_META.map(([p, k]) => (
                    <label key={p} className="usrPerm role">
                      <input type="checkbox" checked={!!modal.draft[p]} disabled={!!modal.draft.view_admin}
                        onChange={() => setModal({ ...modal, draft: applyDeps({ ...modal.draft, [p]: !modal.draft[p] }, p) })} />
                      {tr(k)}
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="usrModalFoot">
              <button className="usrGhostBtn" onClick={() => setModal(null)}>{tr('Cancel')}</button>
              <button className="usrPrimaryBtn" disabled={busy || !canSave} onClick={saveModal}>
                {busy ? "…" : tr('Save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Users;
